import type { Observable } from "rxjs";
import { filter } from "rxjs";
import { Injectable } from "@snap/ts-inject";
import { stringifyError } from "../common/errorHelpers";
import type { CameraKitConfiguration } from "../configuration";
import { configurationToken } from "../configuration";
import { getPlatformInfo } from "../platform/platformInfo";
import { logEntriesFactory } from "./logEntries";
import type { LogEntry } from "./logger";
import { logLevelMap } from "./logger";

/**
 * The factory subscribes to log entry events and, based on the configured log level,
 * forwards matching log entries to the logger specified in the CameraKit configuration object.
 *
 * @internal
 */
export const registerLogEntriesSubscriber = Injectable(
    "registerLogEntriesSubscriber",
    [configurationToken, logEntriesFactory.token] as const,
    (configuration: CameraKitConfiguration, logEntries: Observable<LogEntry>) => {
        logEntries
            .pipe(filter((entry) => logLevelMap[entry.level] >= logLevelMap[configuration.logLevel]))
            .subscribe((logEntry) => {
                switch (configuration.logger) {
                    case "console":
                        // Chrome doesn't print the `cause` Error property, so we need to manually construct a complete
                        // stack trace using our `stringifyError` helper.
                        const messages =
                            getPlatformInfo().browser.brand === "Chrome"
                                ? logEntry.messages.map((message) => {
                                      if (!(message instanceof Error)) return message;
                                      message.stack = stringifyError(message);
                                      return message;
                                  })
                                : logEntry.messages;

                        console[logEntry.level](`[CameraKit.${logEntry.module}]`, ...messages);
                        break;
                }
            });
    }
);
